import { useState } from 'react'
import { useNavigate } from 'react-router'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { billingApiService } from '../services/billingApiService'
import { ApiError } from '../services/BaseApiService'
import { useAuth } from '../contexts/AuthContext'
import { config } from '../config/environment'
import { colors, fontSize, radius, shadow, fontFamily, componentHeight, transition } from '../styles/theme'

const stripePromise = config.stripePublishableKey ? loadStripe(config.stripePublishableKey) : null

/**
 * The signup card gate's destination. A verified Standard-trial team must add a card before the app
 * opens; the trial is already running and nothing is charged during it (the $0 SetupIntent only
 * validates the card). CardGate routes here whenever the team needs a card, so on success we send
 * them to the dashboard and let CardGate re-check.
 */
function GateForm() {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    const element = elements.getElement(CardElement)
    if (!element) return
    setError('')
    setSaving(true)
    try {
      const { clientSecret } = await billingApiService.setupIntent()
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: element } })
      if (result.error) {
        setError(result.error.message ?? 'The card could not be saved')
        return
      }
      const paymentMethodRef = result.setupIntent?.payment_method
      if (typeof paymentMethodRef !== 'string') {
        setError('The card was saved but could not be linked to this team')
        return
      }
      await billingApiService.attachPaymentMethod(paymentMethodRef)
      navigate('/app/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The card could not be saved')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 14, marginBottom: 16, background: colors.surface }}>
        <CardElement options={{ style: { base: { fontSize: '15px', color: colors.text } } }} />
      </div>
      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
          borderRadius: radius.md, padding: '10px 14px', fontSize: fontSize.sm, marginBottom: 16,
        }}>
          {error}
        </div>
      )}
      <button type="submit" disabled={!stripe || saving}
        style={{
          width: '100%', height: componentHeight.btnLg, background: colors.primary, color: '#fff',
          border: 'none', borderRadius: radius.full, fontSize: fontSize.sm, fontWeight: 500,
          cursor: saving ? 'default' : 'pointer', fontFamily: fontFamily.sans,
          transition: `background ${transition.fast}`,
        }}
        onMouseEnter={e => { if (!saving) e.currentTarget.style.background = colors.primaryHover }}
        onMouseLeave={e => { e.currentTarget.style.background = colors.primary }}>
        {saving ? 'Saving…' : 'Start my free trial'}
      </button>
    </form>
  )
}

export default function AddCardGatePage() {
  const { logout } = useAuth()
  const navigate = useNavigate()

  async function handleSignOut() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: colors.surfaceTinted, padding: '24px 16px', fontFamily: fontFamily.sans,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
            <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
            <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
            <path d="M7 9h6M7 12h6M7 15h4" stroke="#0F9D92" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
        </div>
        <span style={{ fontSize: '22px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
          Doc<span style={{ color: colors.primary }}>morph</span>ia
        </span>
      </div>

      <div style={{
        background: colors.surface, borderRadius: radius.lg, border: `1px solid ${colors.border}`,
        boxShadow: shadow.md, padding: '32px 32px 28px', width: '100%', maxWidth: 420,
      }}>
        <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', marginBottom: 8 }}>
          Start your 30-day free trial
        </div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: 20, lineHeight: 1.6 }}>
          Add a card to unlock DocMorphia. You won't be charged during your 30-day trial — we only run a
          $0 validation now. After the trial, your Standard plan bills monthly for what you render, and
          you can cancel any time by removing your card.
        </div>

        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <GateForm />
          </Elements>
        ) : (
          <div style={{ color: colors.error, fontSize: fontSize.sm }}>
            Card entry is unavailable in this environment.
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: fontSize.sm, color: colors.textSecondary }}>
          <button type="button" onClick={handleSignOut}
            style={{ background: 'none', border: 'none', color: colors.primary, fontWeight: 500, cursor: 'pointer', padding: 0, fontFamily: fontFamily.sans, fontSize: fontSize.sm }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
